import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege';
import type { DataSourceId } from 'cadenza/repository/model/data-source-id';
import type { ObjecttypeIdString } from 'cadenza/repository/objecttype-id';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import type { EnrichmentDto } from 'cadenza/workbook/api/enrichment';
import type { DataAcquisitionActionType } from 'cadenza/repository/model/data-acquisition-action-type';
import type { RepositoryName } from 'cadenza/repository/model/repository-name';
import type { AttributeRelationGroupDto } from 'cadenza/workbook/api/attribute-relation-group-dto';

export const ObjecttypeBoundState = {
  /** Objecttype is not bound.*/
  UNBOUND: 'UNBOUND',
  /** Objecttype is bound in a transient state to the workbook.*/
  TRANSIENT: 'TRANSIENT',
  /** Objecttype is bound to the workbook.*/
  BOUND_TO_WORKBOOK: 'BOUND_TO_WORKBOOK'
} as const;

export type ObjecttypeBoundState = keyof typeof ObjecttypeBoundState;

/** @see net/disy/cadenza/workbook/web/dto/ObjecttypeDto.java */
export interface Objecttype {
  id: ObjecttypeIdString;
  printName: string;
  /** Name of data source this object type belongs to. */
  dataSourceId: DataSourceId;
  /** Name of the repository the data source belongs to */
  dataSourceRepositoryName: RepositoryName;
  /** IDs of reachable object types. */
  reachableObjecttypes: ObjecttypeIdString[];
  attributes: AttributeId[];
  /* privileges of this objecttype for current user */
  privileges: ObjectPrivilege[];
  /** Map of objecttype related properties. */
  properties?: { [x: string]: string | number | boolean | null };
  /**
   * If according DataAcquisitionActionType is supported, then the objecttype allows data acquisition
   * including adding, editing and deletion of features.
   */
  possibleDataAcquisitionActionTypes?: DataAcquisitionActionType[];
  /** Bound state of this object type */
  boundState: ObjecttypeBoundState;
  enrichments?: EnrichmentDto[];
  visible: boolean;
  isCombinedObjecttype?: boolean;
  sourceObjecttypeId?: ObjecttypeIdString;
  isMarkedAsTemplate: boolean;
  attributeRelationGroups: AttributeRelationGroupDto[];
}

/**
 * Do you really mean to use this method? It will only say 'true' if the given objecttype
 * is permanently bound to the workbook. Thus, any new objecttype where the objecttype has just been
 * created and the workbook hasn't been saved will be just transiently bound and not included here.
 * @param objecttype The OT to test
 */
export function isObjecttypePermanentAndBound (objecttype: Objecttype) {
  return objecttype.boundState === ObjecttypeBoundState.BOUND_TO_WORKBOOK;
}

/**
 * You will need this version if you care about the OT being in the workbook storage, independent
 * of it being already saved and thus becoming "permanently" bound or still being "transient".
 * @param objecttype The OT to test
 */
export function isObjecttypeBound (objecttype: Objecttype) {
  return objecttype?.boundState !== ObjecttypeBoundState.UNBOUND;
}
